import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import {
  ApiError,
  fetchPortalChecklist,
  fetchPortalDealContact,
  fetchPortalTemplates,
  sendPortal,
} from "../../api";
import type {
  ChecklistResponse,
  PortalBorrower,
  SendPortalRequest,
  TemplateItem,
  TemplatesResponse,
} from "../../types/portal";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface DocPortalSendModalProps {
  dealId: string;
  /**
   * "create" renders the full form (borrower, optional co-borrower, docs,
   * custom items, optional e-sign). "add" hides borrower/co-borrower/e-sign
   * since Portal already has those attached to the existing portal — Portal
   * auto-detects the additional-docs path on its end.
   */
  mode: "create" | "add";
  /** Required for add mode: Portal's /send zod schema requires `borrower`
   *  unconditionally. Parent passes the active portal's borrower so we can
   *  include it in the submit body without re-prompting the user. Unused in
   *  create mode (form collects fresh borrower fields). */
  existingBorrower?: PortalBorrower;
  /** Fires on 200 from /send. Parent closes the modal + toasts + refreshes. */
  onConfirmed: (args: {
    action: "created" | "updated";
    docsAdded: number;
    borrowerEmail?: string;
  }) => void;
  onCancel: () => void;
}

interface CustomItem {
  id: string;
  label: string;
}

interface BorrowerFields {
  first_name: string;
  last_name: string;
  email: string;
}

const EMPTY_BORROWER: BorrowerFields = {
  first_name: "",
  last_name: "",
  email: "",
};

export function DocPortalSendModal({
  dealId,
  mode,
  existingBorrower,
  onConfirmed,
  onCancel,
}: DocPortalSendModalProps) {
  // ---- Initial data ----
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [checklist, setChecklist] = useState<ChecklistResponse | null>(null);
  const [templates, setTemplates] = useState<TemplatesResponse | null>(null);

  // ---- Form state ----
  const [borrower, setBorrower] = useState<BorrowerFields>(EMPTY_BORROWER);
  const [coborrowerOpen, setCoborrowerOpen] = useState(false);
  const [coborrower, setCoborrower] = useState<BorrowerFields>(EMPTY_BORROWER);
  const [checkedDocKeys, setCheckedDocKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const [customItems, setCustomItems] = useState<CustomItem[]>([]);
  const [esignOpen, setEsignOpen] = useState(false);
  const [esignTemplateDocKey, setEsignTemplateDocKey] = useState<string>("");

  // ---- Submit state ----
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null,
  );
  // Lazy — one key per modal session, reused on retry so Portal's 24h dedupe
  // collapses retries into one logical send.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  // ---- Initial fetch (parallel) ----
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    const tasks: Promise<unknown>[] = [
      fetchPortalChecklist().then((c) => {
        if (!cancelled) setChecklist(c);
      }),
      fetchPortalTemplates().then((t) => {
        if (!cancelled) setTemplates(t);
      }),
    ];
    if (mode === "create") {
      tasks.push(
        fetchPortalDealContact(dealId).then((contact) => {
          if (cancelled || !contact) return;
          setBorrower({
            first_name: contact.first_name,
            last_name: contact.last_name,
            email: contact.email,
          });
        }),
      );
    }
    Promise.allSettled(tasks)
      .then((results) => {
        if (cancelled) return;
        // Tolerate /contact failing (no_contact, etc.) — borrower fields just
        // stay empty. But checklist failure (results[0]) is fatal — can't
        // render a doc picker without it. setChecklist already fired inside
        // the .then on fulfilled; on rejected we never set it, so the loader
        // pane stays and we show an error.
        const checklistResult = results[0];
        if (checklistResult.status === "rejected") {
          setLoadError(
            formatErr(checklistResult.reason, "Could not load document checklist"),
          );
        }
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(formatErr(err, "Could not load form data"));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Intentionally narrow deps: mode + dealId define the fetch shape; we don't
    // re-fetch when other state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, mode]);

  // ---- Escape to cancel (when not mid-submit) ----
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, submitting]);

  // ---- Clear stale validation message as user types past it ----
  useEffect(() => {
    if (validationMessage) setValidationMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    borrower.first_name,
    borrower.last_name,
    borrower.email,
    coborrowerOpen,
    coborrower.first_name,
    coborrower.last_name,
    coborrower.email,
    checkedDocKeys,
    customItems,
    esignOpen,
    esignTemplateDocKey,
  ]);

  // ---- Derived ----
  const selectedTemplate: TemplateItem | null = useMemo(() => {
    if (!esignOpen || !esignTemplateDocKey || !templates) return null;
    return (
      templates.templates.find((t) => t.doc_key === esignTemplateDocKey) ?? null
    );
  }, [esignOpen, esignTemplateDocKey, templates]);

  // ---- Form helpers ----
  const toggleDoc = (docKey: string) => {
    setCheckedDocKeys((prev) => {
      const next = new Set(prev);
      if (next.has(docKey)) next.delete(docKey);
      else next.add(docKey);
      return next;
    });
  };
  const addCustomItem = () => {
    setCustomItems((prev) => [
      ...prev,
      { id: crypto.randomUUID(), label: "" },
    ]);
  };
  const updateCustomItem = (id: string, label: string) => {
    setCustomItems((prev) => prev.map((i) => (i.id === id ? { ...i, label } : i)));
  };
  const removeCustomItem = (id: string) => {
    setCustomItems((prev) => prev.filter((i) => i.id !== id));
  };

  // ---- Validate + submit ----
  const handleSubmit = async () => {
    const nonEmptyCustom = customItems.filter((i) => i.label.trim().length > 0);
    const hasDocs = checkedDocKeys.size > 0 || nonEmptyCustom.length > 0;
    if (!hasDocs) {
      setValidationMessage("Pick at least one document or add a custom item.");
      return;
    }
    if (mode === "create") {
      const missing = (b: BorrowerFields) =>
        !b.first_name.trim() || !b.last_name.trim() || !b.email.trim();
      const badEmail = (email: string) => !EMAIL_RE.test(email.trim());
      if (missing(borrower)) {
        setValidationMessage("Borrower first name, last name, and email are required.");
        return;
      }
      if (badEmail(borrower.email)) {
        setValidationMessage("Borrower email is not a valid address.");
        return;
      }
      if (coborrowerOpen) {
        if (missing(coborrower)) {
          setValidationMessage("Co-borrower fields are required when the section is open. Either fill them in or remove the co-borrower.");
          return;
        }
        if (badEmail(coborrower.email)) {
          setValidationMessage("Co-borrower email is not a valid address.");
          return;
        }
      }
      if (esignOpen) {
        if (!selectedTemplate) {
          setValidationMessage("Pick an e-sign template or close the e-sign section.");
          return;
        }
        if (selectedTemplate.recipients > 1 && !coborrowerOpen) {
          setValidationMessage(
            `This template requires ${selectedTemplate.recipients} signers — add a co-borrower above or pick a 1-signer template.`,
          );
          return;
        }
      }
    }

    setValidationMessage(null);
    setError(null);
    setSubmitting(true);

    const body: SendPortalRequest = {
      doc_keys: Array.from(checkedDocKeys),
      custom_items: nonEmptyCustom.map((i) => ({ doc_label: i.label.trim() })),
      // v1.2.2: only doc_key + recipients[]. Portal resolves template_id
      // internally via esign-templates registry. Recipients order is
      // contract: first is borrower, second (when present) is co-borrower.
      esign:
        mode === "create" && esignOpen && selectedTemplate
          ? {
              doc_key: selectedTemplate.doc_key,
              recipients: [
                {
                  first_name: borrower.first_name.trim(),
                  last_name: borrower.last_name.trim(),
                  email: borrower.email.trim(),
                },
                ...(coborrowerOpen
                  ? [
                      {
                        first_name: coborrower.first_name.trim(),
                        last_name: coborrower.last_name.trim(),
                        email: coborrower.email.trim(),
                      },
                    ]
                  : []),
              ],
            }
          : null,
      // Portal's /send zod schema requires `borrower` on BOTH create and add
      // paths. For add mode, use the existing portal's borrower (passed in by
      // parent) instead of re-collecting from the user.
      borrower:
        mode === "create"
          ? {
              first_name: borrower.first_name.trim(),
              last_name: borrower.last_name.trim(),
              email: borrower.email.trim(),
            }
          : {
              first_name: existingBorrower?.first_name ?? "",
              last_name: existingBorrower?.last_name ?? "",
              email: existingBorrower?.email ?? "",
            },
    };

    try {
      const result = await sendPortal(dealId, body, idempotencyKey);
      onConfirmed({
        action: result.action,
        docsAdded: result.docs_added,
        borrowerEmail: mode === "create" ? borrower.email.trim() : undefined,
      });
      // Parent unmounts on success.
    } catch (err) {
      setError(formatErr(err, "Portal could not be sent"));
      setSubmitting(false);
    }
  };

  // ---- Render ----

  if (loading) {
    return (
      <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
        <div className="write-confirm-card doc-portal-send-card">
          <div className="write-confirm-title">
            {mode === "create" ? "SEND DOCUMENT PORTAL" : "ADD DOCUMENTS TO PORTAL"}
          </div>
          <div className="doc-portal-send-loading">
            <span className="chat-thinking-dot" />
            <span className="chat-thinking-dot" />
            <span className="chat-thinking-dot" />
            <span className="chat-thinking-label">LOADING FORM DATA</span>
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
        <div className="write-confirm-card doc-portal-send-card">
          <div className="write-confirm-title">
            {mode === "create" ? "SEND DOCUMENT PORTAL" : "ADD DOCUMENTS TO PORTAL"}
          </div>
          <div className="write-confirm-error">⚠ {loadError}</div>
          <div className="write-confirm-actions">
            <button className="write-confirm-cancel" onClick={onCancel}>
              CLOSE
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card doc-portal-send-card">
        <div className="write-confirm-title">
          {mode === "create" ? "SEND DOCUMENT PORTAL" : "ADD DOCUMENTS TO PORTAL"}
        </div>
        <div className="doc-portal-send-intro">
          {mode === "create"
            ? "The borrower will receive an email with a magic link to upload the documents you select below."
            : "Add more documents to the existing portal. The borrower receives a follow-up email."}
        </div>

        <div className="doc-portal-send-scroll">
          {mode === "create" && (
            <BorrowerSection
              label="BORROWER"
              fields={borrower}
              onChange={setBorrower}
              disabled={submitting}
            />
          )}

          {mode === "create" && (
            <SectionToggle
              label={coborrowerOpen ? "− CO-BORROWER" : "+ ADD CO-BORROWER"}
              open={coborrowerOpen}
              onToggle={() => setCoborrowerOpen((v) => !v)}
              disabled={submitting}
            >
              <BorrowerSection
                label="CO-BORROWER"
                fields={coborrower}
                onChange={setCoborrower}
                disabled={submitting}
                hideLabel
              />
            </SectionToggle>
          )}

          <ChecklistSection
            checklist={checklist!}
            checkedDocKeys={checkedDocKeys}
            onToggleDoc={toggleDoc}
            disabled={submitting}
          />

          <CustomItemsSection
            items={customItems}
            onAdd={addCustomItem}
            onUpdate={updateCustomItem}
            onRemove={removeCustomItem}
            disabled={submitting}
          />

          {mode === "create" && (
            <SectionToggle
              label={esignOpen ? "− E-SIGN PACKET" : "+ ADD E-SIGN PACKET"}
              open={esignOpen}
              onToggle={() => setEsignOpen((v) => !v)}
              disabled={submitting}
            >
              <EsignSection
                templates={templates?.templates ?? []}
                selectedDocKey={esignTemplateDocKey}
                onSelect={setEsignTemplateDocKey}
                borrower={borrower}
                coborrower={coborrowerOpen ? coborrower : null}
                disabled={submitting}
              />
            </SectionToggle>
          )}
        </div>

        {validationMessage && (
          <div className="doc-portal-send-validation">⚠ {validationMessage}</div>
        )}
        {error && (
          <div className="write-confirm-error">
            ⚠ {error}. Try again — same key reused to prevent double-send.
          </div>
        )}

        <div className="write-confirm-actions">
          <button
            className="write-confirm-cancel"
            onClick={onCancel}
            disabled={submitting}
          >
            CANCEL
          </button>
          <button
            className="write-confirm-confirm doc-portal-send-submit"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting
              ? "SENDING…"
              : mode === "create"
                ? "SEND PORTAL"
                : "ADD DOCUMENTS"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Sub-components
// ============================================================

function BorrowerSection({
  label,
  fields,
  onChange,
  disabled,
  hideLabel,
}: {
  label: string;
  fields: BorrowerFields;
  onChange: (next: BorrowerFields) => void;
  disabled: boolean;
  hideLabel?: boolean;
}) {
  return (
    <div className="doc-portal-send-section">
      {!hideLabel && (
        <div className="doc-portal-send-section-label">{label}</div>
      )}
      <div className="doc-portal-send-borrower-row">
        <input
          className="doc-portal-send-input"
          placeholder="First name"
          value={fields.first_name}
          onChange={(e) => onChange({ ...fields, first_name: e.target.value })}
          disabled={disabled}
        />
        <input
          className="doc-portal-send-input"
          placeholder="Last name"
          value={fields.last_name}
          onChange={(e) => onChange({ ...fields, last_name: e.target.value })}
          disabled={disabled}
        />
        <input
          className="doc-portal-send-input"
          placeholder="Email"
          type="email"
          value={fields.email}
          onChange={(e) => onChange({ ...fields, email: e.target.value })}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

function SectionToggle({
  label,
  open,
  onToggle,
  disabled,
  children,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  disabled: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="doc-portal-send-toggle-wrap">
      <button
        className="doc-portal-send-toggle"
        onClick={onToggle}
        disabled={disabled}
        type="button"
      >
        {label}
      </button>
      {open && <div className="doc-portal-send-toggle-body">{children}</div>}
    </div>
  );
}

function ChecklistSection({
  checklist,
  checkedDocKeys,
  onToggleDoc,
  disabled,
}: {
  checklist: ChecklistResponse;
  checkedDocKeys: Set<string>;
  onToggleDoc: (docKey: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="doc-portal-send-section">
      <div className="doc-portal-send-section-label">
        DOCUMENTS{" "}
        <span className="doc-portal-send-section-count">
          ({checkedDocKeys.size} selected)
        </span>
      </div>
      <div className="doc-portal-send-checklist">
        {checklist.categories.map((cat) => (
          <div className="doc-portal-send-cat" key={cat.key}>
            <div className="doc-portal-send-cat-header">{cat.display_name}</div>
            {cat.items.map((item) => (
              <label
                key={item.doc_key}
                className={clsx(
                  "doc-portal-send-item",
                  checkedDocKeys.has(item.doc_key) && "is-checked",
                )}
              >
                <input
                  type="checkbox"
                  className="doc-portal-send-checkbox"
                  checked={checkedDocKeys.has(item.doc_key)}
                  onChange={() => onToggleDoc(item.doc_key)}
                  disabled={disabled}
                />
                <span className="doc-portal-send-item-label">
                  {item.display_name}
                </span>
              </label>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomItemsSection({
  items,
  onAdd,
  onUpdate,
  onRemove,
  disabled,
}: {
  items: CustomItem[];
  onAdd: () => void;
  onUpdate: (id: string, label: string) => void;
  onRemove: (id: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="doc-portal-send-section">
      <div className="doc-portal-send-section-label">CUSTOM DOCS</div>
      {items.length === 0 && (
        <div className="doc-portal-send-custom-empty">
          No custom items — add anything not in the checklist above.
        </div>
      )}
      {items.map((item) => (
        <div className="doc-portal-send-custom-row" key={item.id}>
          <input
            className="doc-portal-send-input"
            placeholder="e.g. Property survey 2026"
            value={item.label}
            onChange={(e) => onUpdate(item.id, e.target.value)}
            disabled={disabled}
          />
          <button
            className="doc-portal-send-custom-remove"
            onClick={() => onRemove(item.id)}
            disabled={disabled}
            type="button"
            title="Remove custom item"
            aria-label="Remove custom item"
          >
            ×
          </button>
        </div>
      ))}
      <button
        className="doc-portal-send-custom-add"
        onClick={onAdd}
        disabled={disabled}
        type="button"
      >
        + ADD CUSTOM DOC
      </button>
    </div>
  );
}

function EsignSection({
  templates,
  selectedDocKey,
  onSelect,
  borrower,
  coborrower,
  disabled,
}: {
  templates: TemplateItem[];
  selectedDocKey: string;
  onSelect: (docKey: string) => void;
  borrower: BorrowerFields;
  coborrower: BorrowerFields | null;
  disabled: boolean;
}) {
  const selected = templates.find((t) => t.doc_key === selectedDocKey) ?? null;
  return (
    <div className="doc-portal-send-esign">
      <label className="doc-portal-send-esign-template">
        <span>Template</span>
        <select
          className="doc-portal-send-select"
          value={selectedDocKey}
          onChange={(e) => onSelect(e.target.value)}
          disabled={disabled}
        >
          <option value="">— pick a template —</option>
          {templates.map((t) => (
            <option key={t.doc_key} value={t.doc_key}>
              {t.display_name} ({t.recipients} signer
              {t.recipients === 1 ? "" : "s"})
            </option>
          ))}
        </select>
      </label>
      {selected && (
        <div className="doc-portal-send-esign-recipients">
          <div className="doc-portal-send-section-label">RECIPIENTS</div>
          <div className="doc-portal-send-esign-recipient">
            <span className="doc-portal-send-esign-role">Borrower:</span>
            <span>
              {borrower.first_name} {borrower.last_name}{" "}
              {borrower.email && <>&lt;{borrower.email}&gt;</>}
            </span>
          </div>
          {coborrower && (
            <div className="doc-portal-send-esign-recipient">
              <span className="doc-portal-send-esign-role">Co-Borrower:</span>
              <span>
                {coborrower.first_name} {coborrower.last_name}{" "}
                {coborrower.email && <>&lt;{coborrower.email}&gt;</>}
              </span>
            </div>
          )}
          {selected.recipients > 1 && !coborrower && (
            <div className="doc-portal-send-validation">
              ⚠ This template requires {selected.recipients} signers — add a
              co-borrower above.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================

function formatErr(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    // Portal v1.2 validation errors come through with `issues: [{path, message}]`
    // — render field-level detail when present. Falls back to the code/status
    // line for non-validation errors.
    const body = err.body as
      | { issues?: Array<{ path?: unknown; message?: unknown }> }
      | undefined;
    if (Array.isArray(body?.issues) && body.issues.length > 0) {
      const list = body.issues
        .map((i) => {
          const path = typeof i.path === "string" ? i.path : Array.isArray(i.path) ? i.path.join(".") : "";
          const msg = typeof i.message === "string" ? i.message : "invalid";
          return path ? `${path}: ${msg}` : msg;
        })
        .join("; ");
      return `${fallback}: ${list}`;
    }
    return `${fallback}: ${err.code} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return `${fallback}: ${err.message}`;
  if (err == null) return fallback;
  if (typeof err === "string") return `${fallback}: ${err}`;
  try {
    return `${fallback}: ${JSON.stringify(err)}`;
  } catch {
    return `${fallback}: ${String(err)}`;
  }
}
